import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { BookOpen, Globe } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { CitationResponse } from '@/api/types'

interface CitationBadgeProps {
  citation: CitationResponse
  onClick?: () => void
}

export function CitationBadge({ citation, onClick }: CitationBadgeProps) {
  const { t } = useTranslation()
  const [isNew, setIsNew] = useState(true)
  const isWebSource = citation.source_type === 'web'

  // Determine if this is a non-document source (medication, terminology)
  // Check source_type first, fall back to document name patterns for older data
  const isNonDocumentSource =
    citation.source_type === 'medication' ||
    citation.source_type === 'terminology' ||
    citation.document?.startsWith('SNOMED:') ||
    citation.document?.startsWith('SAI:')

  // Check if this is a fallback/unknown citation (shouldn't show page numbers)
  const isUnknownSource = citation.document === 'Unknown Document'

  // Only show page numbers when they're meaningful (not the 1-1 default)
  const hasRealPageNumbers = citation.page_start > 0 &&
    !(citation.page_start === 1 && citation.page_end === 1)

  // Only show page numbers for actual document sources with known titles and real page numbers
  const showPageNumbers = !isWebSource && !isNonDocumentSource && !isUnknownSource && hasRealPageNumbers

  useEffect(() => {
    const timer = setTimeout(() => setIsNew(false), 600)
    return () => clearTimeout(timer)
  }, [])

  // Open URL for any citation that has one (web sources, knowledge base docs with page links)
  // Fall back to sheet for citations without URLs
  const handleClick = () => {
    if (citation.url) {
      window.open(citation.url, '_blank', 'noopener,noreferrer')
    } else if (onClick) {
      onClick()
    }
  }

  return (
    <button
      type="button"
      data-testid="citation-badge"
      onClick={handleClick}
      className={cn(
        "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full",
        "min-h-[32px]",
        "text-xs font-medium",
        "transition-colors duration-150",
        isWebSource
          ? "bg-web-citation-50 text-web-citation-700 border border-web-citation-300 hover:bg-web-citation-100 active:bg-web-citation-100"
          : "bg-citation-50 text-citation-800 border border-citation-200 hover:bg-citation-100 active:bg-citation-150",
        isNew && "animate-citation-glow"
      )}
    >
      {isWebSource ? (
        <Globe className="w-3 h-3" />
      ) : (
        <BookOpen className="w-3 h-3" />
      )}
      <span className="truncate max-w-[120px]">{citation.document}</span>
      {isWebSource ? (
        <span className="text-web-citation-500">(Web)</span>
      ) : showPageNumbers ? (
        <span className="text-citation-500">{t('source.pageRange', { start: citation.page_start, end: citation.page_end })}</span>
      ) : null}
    </button>
  )
}
